<script setup lang="ts">
/**
 * Narratives workspace — the page behind the "Narratives" item in the matter
 * nav rail. Reuses the same shell as MatterPage (far-left icon rail + section
 * tab bar + collapsible/resizable sub-nav + main content). Lists the matter's
 * narratives (NarrativeList, from the stub narratives API via narrativesStore)
 * and shows an empty-state hero when there are none. Creating a narrative (from
 * the hero, the collapsed rail, or the sub-nav's add button) or clicking an
 * existing one navigates to its detail/editor page (NarrativeDetailPage). This
 * page owns the list; the detail page owns a single open narrative.
 */

import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import MatterNavRail from '../matter/MatterNavRail.vue';
import NarrativeTabs from './NarrativeTabs.vue';
import NarrativeList from './NarrativeList.vue';
import { useMatterStore } from '../../stores/matterStore';
import { useNarrativesStore } from '../../stores/narrativesStore';
import { useResizableSidebar } from '../../composables/useResizableSidebar';

const { t } = useI18n();
const router = useRouter();
const matterStore = useMatterStore();
const store = useNarrativesStore();

const matterId = computed(() => matterStore.currentMatter?.RecordId ?? null);

// Load the narrative list on mount and whenever the active matter changes. The
// store skips the fetch on a same-matter revisit.
watch(
    matterId,
    (id) => {
        if (!id) {
            store.reset();
            return;
        }
        if (store.loadedMatterId !== id) {
            void store.fetchNarratives();
        }
    },
    { immediate: true },
);

// No side panels on this page, so the sidebar never auto-collapses; the header
// toggle is the only way to collapse it. Shares the persisted width with the
// Dialogues sidebar via useResizableSidebar's localStorage key.
const {
    width: sidebarWidth,
    collapsed: navCollapsed,
    startResize: startSidebarResize,
    toggleCollapsed: toggleNav,
} = useResizableSidebar();

// Bumped to ask NarrativeList to open + focus its search box from the
// collapsed rail (which expands the sidebar first). Mirrors MatterPage.
const searchTrigger = ref(0);

const requestSearch = () => {
    if (navCollapsed.value) toggleNav();
    searchTrigger.value += 1;
};

const creating = ref(false);

const goToNarrative = (narrativeId: string) => {
    const id = matterId.value;
    if (id) router.push({ name: 'narrative-detail', params: { matterId: id, narrativeId } });
};

// Create a stub narrative (seeded with one page) and navigate into it — the
// mockup lands on an already-created "Untitled narrative".
const startNewNarrative = async () => {
    if (creating.value || !matterId.value) return;
    creating.value = true;
    try {
        const narrative = await store.createNarrative();
        goToNarrative(narrative.id);
    } catch {
        // Store surfaces the error.
    } finally {
        creating.value = false;
    }
};
</script>

<template>
    <div class="matter-page">
        <MatterNavRail />

        <div class="narrative-workspace">
            <NarrativeTabs />

            <div class="narrative-workspace-body">
                <transition name="mc-strip">
                    <nav
                        v-if="navCollapsed"
                        class="dialogues-nav-collapsed"
                        :aria-label="t('narratives.collapsedAriaLabel')"
                    >
                        <button
                            type="button"
                            class="dialogues-collapsed-item"
                            :title="t('narratives.expand')"
                            :aria-label="t('narratives.expand')"
                            @click="toggleNav"
                        >
                            <span class="material-icons">chevron_right</span>
                        </button>

                        <button
                            type="button"
                            class="dialogues-collapsed-item"
                            :title="t('narratives.newNarrative')"
                            :aria-label="t('narratives.newNarrative')"
                            @click="startNewNarrative"
                        >
                            <span class="material-icons">add</span>
                        </button>

                        <button
                            type="button"
                            class="dialogues-collapsed-item"
                            :title="t('narratives.search')"
                            :aria-label="t('narratives.search')"
                            @click="requestSearch"
                        >
                            <span class="material-icons">search</span>
                        </button>
                    </nav>
                </transition>

                <transition name="mc-sidebar">
                    <div
                        v-if="!navCollapsed"
                        class="sidebar"
                        :style="{ width: sidebarWidth + 'px' }"
                    >
                        <div class="matter-sidebar-header">
                            <button
                                type="button"
                                class="sidebar-collapse-btn"
                                :title="t('matters.sidebar.collapse')"
                                :aria-label="t('matters.sidebar.collapse')"
                                @click="toggleNav"
                            >
                                <svg class="nav-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <line x1="19" y1="5" x2="19" y2="19" />
                                    <line x1="13" y1="12" x2="4" y2="12" />
                                    <polyline points="8 8 4 12 8 16" />
                                </svg>
                            </button>
                        </div>
                        <NarrativeList
                            :search-trigger="searchTrigger"
                            @new-narrative="startNewNarrative"
                            @select="goToNarrative"
                        />
                        <div
                            class="sidebar-resize-handle"
                            :title="t('matters.sidebar.resize')"
                            @mousedown="startSidebarResize"
                        ></div>
                    </div>
                </transition>

                <div class="main-content">
                    <div class="chat-section">
                        <!-- Empty-state hero (no narratives yet). -->
                        <div v-if="store.narratives.length === 0" class="narratives-empty">
                            <span class="material-icons-outlined narratives-empty-icon"
                                >auto_stories</span
                            >
                            <h2 class="narratives-empty-title">{{ t('narratives.hero.title') }}</h2>
                            <p class="narratives-empty-message">
                                {{ t('narratives.hero.message') }}
                            </p>
                            <button
                                type="button"
                                class="narratives-empty-button"
                                :disabled="creating"
                                @click="startNewNarrative"
                            >
                                <span class="material-icons">add</span>
                                <span>{{ t('narratives.newNarrative') }}</span>
                            </button>
                        </div>

                        <!-- With narratives, the hero collapses to a lightweight
                             prompt; the list lives in the sidebar. -->
                        <div v-else class="narratives-empty">
                            <span class="material-icons-outlined narratives-empty-icon"
                                >auto_stories</span
                            >
                            <h2 class="narratives-empty-title">{{ t('narratives.hero.title') }}</h2>
                            <p class="narratives-empty-message">
                                {{ t('narratives.landing.pickOrCreate') }}
                            </p>
                            <button
                                type="button"
                                class="narratives-empty-button"
                                :disabled="creating"
                                @click="startNewNarrative"
                            >
                                <span class="material-icons">add</span>
                                <span>{{ t('narratives.newNarrative') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
